import React from "react";
import TextButton from "./TextButton";
import { orderStatus } from "../../constants/commonConstants";
import { Trash2 } from "lucide-react";
import {
  currencyFormatter,
  formatOrderStatus,
  getUptoFourteenDaysLater,
} from "../../utils/formatting";
import { formatYYYY_MM_DD, formatYYYY_MM_DD_HH_MM_A } from "../../utils/dateFormating";

const OrdersRow = ({
  order,
  onViewOrder,
  setSelectedFullPreview,
  setOpenModal,
  buttonText,
  secondaryButtonText,
  onSecondaryViewOrder,
  onDeleteDraft,
  tableName,
}) => {
  // Determine the orderStatus color
  const getOrderStatusColor = (status) => {
    if (
      [
        orderStatus.PENDING_PAYMENT,
        orderStatus.IN_REVISION,
        orderStatus.COMPLAINT_RAISED,
      ].includes(status)
    ) {
      return "text-light-red";
    } else if (status === orderStatus.ORDER_IN_PROGRESS) {
      return "text-[#06BCCC]";
    } else if (
      [
        orderStatus.ORDER_DELIVERED,
        orderStatus.RESOLVED,
      ].includes(status)
    ) {
      return "text-blue-shades";
    } else if (status === orderStatus.CANCELLED) {
       return "text-red-500";
    } else {
      return "text-gray-500"; // Default color if no status matches
    }
  };

  const getOrderStatusText = (status) => {
    if (
      status === orderStatus.PENDING_PAYMENT &&
      (tableName === orderStatus.PENDING_PAYMENT || tableName === "MY_ORDERS")
    ) {
      return "Draft";
    }

    // Check if the status exists in the orderStatus object
    if (orderStatus[status]) {
      return formatOrderStatus(orderStatus[status]);
    } else {
      return status || "Unknown Status"; // Return a fallback string if undefined
    }
  };

  // Check if content is truncated
  const isTruncated = (element) => {
    return element.scrollWidth > element.clientWidth;
  };

  const handleMouseEnter = (e) => {
    const element = e.target;
    if (isTruncated(element)) {
      element.setAttribute("title", element.textContent);
    } else {
      element.removeAttribute("title");
    }
  };

  const isButtonDisabled = () => {
    if (tableName === orderStatus.COMPLAINT_RAISED) {
      const completedDate = order?.completedDate;
      if (!completedDate) return true; // Disable if no deadline

      const fourteenDaysLater = getUptoFourteenDaysLater(completedDate);
      const currentDate = new Date();

      return (
        order?.status === orderStatus.RESOLVED || order?.status === orderStatus.COMPLAINT_RAISED ||
        (order?.status === orderStatus.COMPLETED &&
          fourteenDaysLater < currentDate)
      );
    }
  };

  return (
    <tr className="h-[52px] normalContent text-[13px] text-gray leading-5 font-normal font-inter">
      <td className="px-3">{order?.id}</td>
      <td
        className="px-3 min-w-[120px] max-w-[220px] break-words cursor-pointer"
        onClick={() => {
          setSelectedFullPreview(order?.orderRequirements?.subject);
          setOpenModal(true);
        }}
        onMouseEnter={handleMouseEnter} // Add tooltip logic
      >
        {order?.orderRequirements?.subject}
      </td>
      <td className="px-3 whitespace-nowrap tabular-nums">{currencyFormatter.format(order?.price || 0)}</td>
      <td className={`px-3 max-w-[130px] ${getOrderStatusColor(order?.status)}`}>
        {getOrderStatusText(order?.status)}
      </td>
      <td className="px-3">{formatYYYY_MM_DD(order?.createdAt || '') || ''}</td>
      {
        order?.status === orderStatus.CANCELLED ? 
        <td className="px-3 whitespace-nowrap">
          {formatYYYY_MM_DD(order?.updatedAt || '') || ''}
        </td> 
        : 
        <td className="px-3 whitespace-nowrap">
          {
            tableName === "ALL" ||
            order?.status === orderStatus.ORDER_IN_PROGRESS || 
            order?.status === orderStatus.ORDER_DELIVERED || 
            order?.status === orderStatus.PENDING_WRITER || 
            order?.status === orderStatus.IN_REVISION ||
            order?.status === orderStatus.PENDING_PAYMENT ? 
            formatYYYY_MM_DD_HH_MM_A(order?.orderRequirements?.deadline || '') || '' 
            :formatYYYY_MM_DD(order?.completedDate || '') || ''
          }
        </td>
      }
      <td className={`px-3 whitespace-nowrap ${secondaryButtonText ? "min-w-[230px]" : ""}`}>
        <div className="flex items-center gap-2">
          {secondaryButtonText && onSecondaryViewOrder && (
            <TextButton
              onClick={() => onSecondaryViewOrder(order?.id, order?.threadId, order?._id)}
              text={secondaryButtonText}
              width="max-content"
              height="32px"
            />
          )}
          <TextButton
            onClick={() => onViewOrder(order?.id, order?.threadId, order?._id)}
            text={
              (order?.status === orderStatus.RESOLVED && tableName === orderStatus.COMPLAINT_RAISED)? 
              formatOrderStatus(orderStatus.RESOLVED) : 
              (order?.status === orderStatus.COMPLAINT_RAISED && tableName === orderStatus.COMPLAINT_RAISED) ? 
              "Already Raised" : 
              buttonText || "View Order"
            }
            width={secondaryButtonText ? "max-content" : "127px"}
            height="32px"
            disabled={isButtonDisabled()}
          />
          {tableName === orderStatus.PENDING_PAYMENT && onDeleteDraft && (
            <button
              type="button"
              onClick={() => onDeleteDraft(order)}
              className="flex h-8 w-8 items-center justify-center rounded-[5px] border border-red-100 bg-red-50 text-red-600 transition-colors hover:bg-red-100"
              aria-label={`Delete draft ${order?.id || ""}`}
              title="Delete draft"
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

export default OrdersRow;
